import { useState } from 'react';
import { Card, Icon } from '@evogard/ui';
import type { SalesTarget } from '@evogard/contract';
import { periodLabel } from './common.js';

/** Uma competência da série, já reduzida ao que a linha desenha. */
interface Month {
  readonly period: string;
  readonly label: string;
  /** a meta em número de vendas — a série só existe para quem a declarou assim */
  readonly target: number;
  /** o realizado, ou `null` quando aquela competência não pôde ser medida */
  readonly achieved: number | null;
}

/**
 * A caixa do desenho, em unidades do `viewBox`.
 *
 * **Achatada de propósito.** A primeira versão era `640×180`, e o `viewBox`
 * preserva proporção: numa coluna de ~900px aquilo virava 250px de altura para
 * mostrar sete números. Série temporal curta se lê na horizontal — o que
 * importa é a inclinação entre dois meses, e altura sobrando só afasta o
 * primeiro ponto do último.
 */
const HEIGHT = 132;
const WIDTH = 720;
/**
 * As laterais seguram **meio rótulo**, e não o traço da linha.
 *
 * Eram 14, e a conta não fechava: o rótulo é centrado no ponto, "set/26" ocupa
 * cerca de 30 unidades, e o primeiro e o último saíam pela metade para fora do
 * `viewBox` — cortados sem nada avisando, porque o SVG simplesmente não desenha
 * o que passa da caixa. 34 é metade do rótulo mais largo com folga.
 */
const MARGIN = { top: 18, right: 34, bottom: 22, left: 34 } as const;

/**
 * A largura mínima de uma competência, em `rem`.
 *
 * É o que transforma "espremer sete meses na coluna" em **rolagem lateral**.
 * Sem ela o `viewBox` encolhia tudo junto: os rótulos ficavam ilegíveis e os
 * pontos colados, e a figura dizia menos quanto mais dado tinha — o contrário
 * do que uma série deve fazer. Com ela, a partir de certa quantidade a barra
 * aparece e cada mês mantém o seu espaço.
 */
const MONTH_WIDTH = 8;

/**
 * O comparativo da própria meta: o que foi declarado, e o que foi feito.
 *
 * ## Uma unidade só, e é por isso que as duas linhas cabem no mesmo eixo
 *
 * As duas séries são **número de vendas** — a meta declarada em quantidade e o
 * realizado que a conta do perfil produz. Dois eixos verticais é o erro mais
 * comum em gráfico de painel, e ele nasce exatamente aqui: alguém põe o valor em
 * dinheiro na mesma figura e escala os dois independentemente, e a partir daí o
 * cruzamento das linhas passa a ser um acidente de escala em vez de um fato.
 *
 * Por isso **a competência cuja meta é só em dinheiro fica de fora da série**:
 * ela não tem o que comparar nesta figura. O cartão acima continua mostrando o
 * valor dela.
 *
 * ## A meta é uma REFERÊNCIA, e não a segunda categoria
 *
 * Ela é tracejada e usa cor de texto, não uma segunda cor de série. Duas razões,
 * e a segunda decide: a primeira é que a paleta da casa não tem um segundo tom
 * categórico que sobreviva à prova de daltonismo ao lado da primária; a segunda é
 * que meta e realizado **não são duas categorias** — um é o alvo do outro. Pintar
 * as duas como pares faria a leitura "qual das duas ganhou", que é o veredito que
 * esta tela recusa desde que existe.
 *
 * E por isso nenhuma das duas é verde ou vermelha: `sucesso` e `erro` são cores
 * de estado, e o estado que elas anunciariam — bateu, não bateu — é a pergunta
 * que só quem definiu a meta responde.
 *
 * ## A dica é o lugar dos números, e o rótulo fixo é só do último
 *
 * Número em cada ponto transforma a linha numa tabela mal diagramada. A leitura
 * exata mora na **dica**, que aparece ao passar o ponteiro e traz a competência,
 * o realizado e a meta lado a lado — que é a comparação que a figura existe para
 * fazer. Fixo, só o último ponto: é a competência que a pessoa está olhando.
 */
export function TargetTrend({ history }: { history: readonly SalesTarget[] }) {
  const [hovered, setHovered] = useState<number | null>(null);

  const months: Month[] = history
    .filter((t) => t.quantity !== null)
    .map((t) => ({
      period: t.period,
      label: periodLabel(t.period),
      target: t.quantity as number,
      achieved: t.attainment.estado === 'medido' ? t.attainment.quantity : null,
    }));

  /* Uma linha precisa de dois pontos. Com um, o cartão acima já disse tudo o que
   * há para dizer, e uma figura de um ponto é ruído com moldura. */
  if (months.length < 2) return null;

  const cap = Math.max(...months.flatMap((m) => [m.target, m.achieved ?? 0]), 1);
  const inner = {
    width: WIDTH - MARGIN.left - MARGIN.right,
    height: HEIGHT - MARGIN.top - MARGIN.bottom,
  };
  const x = (i: number) =>
    MARGIN.left + (inner.width * i) / Math.max(months.length - 1, 1);
  const y = (v: number) => MARGIN.top + inner.height * (1 - v / cap);

  const path = (pick: (m: Month) => number | null) =>
    months
      .map((m, i) => {
        const v = pick(m);
        return v === null ? null : `${x(i)},${y(v)}`;
      })
      .filter((p): p is string => p !== null)
      .join(' ');

  const last = months[months.length - 1]!;
  const shown = hovered === null ? null : months[hovered]!;

  /** A altura da marca mais alta da competência — é dela que a dica pende. */
  const anchorY = (m: Month) => y(Math.max(m.target, m.achieved ?? 0));
  /**
   * A dica cai para baixo quando a marca está na metade de cima.
   *
   * Sem isto ela some: o contêiner do desenho recorta, e uma competência de
   * valor alto encosta a marca no topo — a dica ia inteira para fora e o
   * ponteiro sobre o ponto não mostrava nada.
   */
  const below = (m: Month) => anchorY(m) < HEIGHT / 2;

  return (
    /*
     * `as="figure"` e `bleed`: a casca é a do `Card` — a mesma do cartão da meta
     * logo acima —, e o elemento continua sendo um `<figure>`, porque o desenho
     * tem legenda. A sangria é o que deixa o desenho encostar nas bordas.
     */
    <Card
      as="figure"
      bleed
      level={3}
      title={
        <span className="flex items-center gap-2 uppercase">
          <Icon nome="grafico" className="text-texto-suave" />
          Avanço de metas — número de vendas
        </span>
      }
      actions={
        /* A legenda existe sempre que há duas séries: identidade nunca pode
           depender só da cor. */
        <ul className="flex shrink-0 items-center gap-4 text-xs font-semibold text-texto-suave">
          <li className="flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded-full bg-primaria" aria-hidden="true" />
            Realizado
          </li>
          <li className="flex items-center gap-1.5">
            <span
              className="h-0 w-4 border-t-2 border-dashed border-texto-tenue"
              aria-hidden="true"
            />
            Meta
          </li>
        </ul>
      }
    >
      {/*
       * `rolagem-lateral`, e não um `overflow-x-auto` cru.
       *
       * A primeira versão usava o utilitário do Tailwind, e o corte não tinha
       * afordância nenhuma: a linha terminava no meio e **nada dizia que havia
       * mais à direita**. A casa já tinha resolvido isso para a tabela, com
       * quatro camadas de fundo e zero JavaScript — a sombra aparece exatamente
       * quando há o que revelar e some quando não há. Reusar é o ponto: a
       * segunda implementação da mesma afordância divergiria na primeira troca
       * de paleta.
       *
       * `py-6` não é respiro: é o **espaço onde a dica cabe**. A rolagem
       * recorta nos DOIS eixos — não existe rolar na horizontal sem cortar na
       * vertical —, e a dica é HTML posicionado a partir da marca. Numa coluna
       * estreita o desenho tem ~76px de altura e a dica ~46px: sem folga ela
       * estourava a caixa e sumia, mesmo virando para o lado certo. 24px em
       * cima e embaixo cobrem o pior caso das duas viradas.
       */}
      <div className="rolagem-lateral px-4 py-6">
        {/*
         * `relative` porque a dica é HTML posicionado por cima, e não um
         * `<title>` de SVG: o nativo demora ~1s para abrir, não se estiliza e
         * não cabe duas linhas. Como o `viewBox` escala com a largura, a posição
         * vai em PORCENTAGEM — assim ela acompanha o desenho em qualquer coluna
         * sem ninguém medir pixel.
         */}
        <div
          className="relative"
          style={{ minWidth: `${Math.max(24, months.length * MONTH_WIDTH)}rem` }}
        >
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="h-auto w-full"
            role="img"
            aria-label={`Comparativo entre a meta declarada e o realizado, de ${months[0]!.label} a ${last.label}.`}
          >
            {/* grade recessiva: horizontal, tracejada, sem eixo desenhado */}
            {[0, 0.5, 1].map((f) => (
              <line
                key={f}
                x1={MARGIN.left}
                x2={WIDTH - MARGIN.right}
                y1={y(cap * f)}
                y2={y(cap * f)}
                strokeDasharray="4 6"
                className="stroke-borda"
              />
            ))}

            <polyline
              points={path((m) => m.target)}
              fill="none"
              strokeWidth="2"
              strokeDasharray="5 5"
              strokeLinecap="round"
              className="stroke-texto-tenue"
            />
            <polyline
              points={path((m) => m.achieved)}
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-primaria-texto"
            />

            {months.map((m, i) => (
              <g key={m.period}>
                {hovered === i && (
                  <line
                    x1={x(i)}
                    x2={x(i)}
                    y1={MARGIN.top}
                    y2={HEIGHT - MARGIN.bottom}
                    className="stroke-borda-forte"
                  />
                )}

                <circle
                  cx={x(i)}
                  cy={y(m.target)}
                  r="3"
                  className="fill-superficie stroke-texto-tenue"
                  strokeWidth="2"
                />
                {m.achieved !== null && (
                  <circle
                    cx={x(i)}
                    cy={y(m.achieved)}
                    r={hovered === i ? 5 : 4}
                    /* anel da superfície entre as marcas que se cruzam: sem ele,
                       realizado e meta no mesmo valor viram um borrão */
                    className="fill-superficie stroke-primaria-texto"
                    strokeWidth="2"
                  />
                )}

                {/* alvo de toque bem maior que a marca, e é ele que abre a dica */}
                <rect
                  x={x(i) - inner.width / (months.length * 2)}
                  y={0}
                  width={inner.width / months.length}
                  height={HEIGHT}
                  fill="transparent"
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                />

                <text
                  x={x(i)}
                  y={HEIGHT - 6}
                  textAnchor="middle"
                  className="fill-texto-suave text-[0.625rem] font-medium"
                >
                  {m.label}
                </text>
              </g>
            ))}

            {/* o rótulo do ÚLTIMO ponto, e só dele — ver o cabeçalho */}
            {last.achieved !== null && hovered === null && (
              <text
                x={x(months.length - 1)}
                y={y(last.achieved) - 9}
                textAnchor="end"
                className="fill-texto text-[0.625rem] font-bold"
              >
                {last.achieved}
              </text>
            )}
          </svg>

          {shown !== null && hovered !== null && (
            <div
              /*
               * `shadow-flutuante` e não `shadow-cartao`, desde 17/09: a dica
               * paira sobre o desenho, e o `DESIGN.md` reserva a sombra do
               * cartão para a folha que se levanta da página. A régua
               * `cartao-a-mao` achou isto na primeira execução dela — a sombra
               * do cartão fora de um cartão.
               */
              className="pointer-events-none absolute z-10 rounded-md border border-borda bg-fundo px-2.5 py-1.5 shadow-flutuante"
              style={{
                left: `${(x(hovered) / WIDTH) * 100}%`,
                /*
                 * A âncora é o ponto MAIS ALTO da competência — a dica não pode
                 * cobrir a marca que ela explica.
                 */
                top: `${(anchorY(shown) / HEIGHT) * 100}%`,
                /*
                 * Duas viradas, e as duas nasceram de a dica sumir.
                 *
                 * **Na horizontal:** a do último ponto encostava na borda
                 * direita e a do primeiro na esquerda — o deslocamento segue a
                 * posição em vez de um `translate` fixo.
                 *
                 * **Na vertical:** ela ficava sempre ACIMA do ponto, e o
                 * contêiner do desenho tem `overflow` — competência com valor
                 * alto põe a marca junto ao topo, e a dica inteira ia para fora
                 * e era recortada. Sem aviso nenhum: o ponteiro estava sobre o
                 * ponto e nada aparecia. Acima da metade da caixa ela cai para
                 * BAIXO da marca, onde sempre há espaço.
                 */
                transform: `translate(${
                  hovered === 0 ? '0' : hovered === months.length - 1 ? '-100%' : '-50%'
                }, ${below(shown) ? 'calc(8px)' : 'calc(-100% - 8px)'})`,
              }}
            >
              <p className="text-xs font-bold text-texto">{shown.label}</p>
              <dl className="mt-0.5 flex items-center gap-3 whitespace-nowrap text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-primaria" aria-hidden="true" />
                  <dt className="text-texto-suave">Realizado</dt>
                  <dd className="font-bold text-texto tabular-nums">
                    {shown.achieved === null ? 'não medido' : shown.achieved}
                  </dd>
                </div>
                <div className="flex items-center gap-1.5">
                  <span
                    className="h-0 w-3 border-t-2 border-dashed border-texto-tenue"
                    aria-hidden="true"
                  />
                  <dt className="text-texto-suave">Meta</dt>
                  <dd className="font-bold text-texto tabular-nums">{shown.target}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </div>

      {/*
       * A tabela do mesmo dado, para quem lê por leitor de tela e para quem
       * precisa do número exato sem caçar com o ponteiro. Ela não é um extra:
       * gráfico sem alternativa textual é dado que existe só para quem enxerga
       * a figura — e a dica, que é o caminho do mouse, não tem foco de teclado.
       */}
      <figcaption className="sr-only">
        <table>
          <caption>Meta e realizado por competência</caption>
          <thead>
            <tr>
              <th scope="col">Competência</th>
              <th scope="col">Meta</th>
              <th scope="col">Realizado</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.period}>
                <th scope="row">{m.label}</th>
                <td>{m.target}</td>
                <td>{m.achieved === null ? 'não medido' : m.achieved}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </figcaption>
    </Card>
  );
}
